// frontend/src/components/Booking/BookingFilters.tsx
// Mobile-first functional booking filters

import React, { useState } from 'react';
import {
  Search,
  Calendar,
  Building2,
  Users,
  Package,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { BookingFilters as IBookingFilters, BookingFiltersProps, BookingStatus } from '@/types/booking';

const BookingFilters: React.FC<BookingFiltersProps> = ({
  filters,
  onFilterChange,
  isLoading = false,
  showAdminFilters = false
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const statusOptions: Array<{ value: BookingStatus | 'all'; label: string; color?: string }> = [
    { value: 'all', label: 'All Status', color: 'text-gray-600' },
    { value: 'pending', label: 'Pending', color: 'text-amber-600' },
    { value: 'accepted', label: 'Active', color: 'text-blue-600' },
    { value: 'completed', label: 'Completed', color: 'text-green-600' },
    { value: 'rejected', label: 'Rejected', color: 'text-red-600' },
    { value: 'cancelled', label: 'Cancelled', color: 'text-gray-600' }
  ];

  const handleInputChange = (field: keyof IBookingFilters, value: string) => {
    onFilterChange({ [field]: value });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange('search', e.target.value);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleInputChange('status', e.target.value as BookingStatus | 'all');
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange('date_from', e.target.value);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange('date_to', e.target.value);
  };

  const handleProviderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange('provider_id', e.target.value);
  };

  const handleCustomerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange('customer_id', e.target.value);
  };

  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange('service_type', e.target.value);
  };

  const clearFilters = () => {
    onFilterChange({
      search: '',
      status: 'all',
      date_from: '',
      date_to: '',
      provider_id: '',
      customer_id: '',
      service_type: ''
    });
  };

  const hasActiveFilters = filters.search || 
                          filters.status !== 'all' || 
                          filters.date_from || 
                          filters.date_to ||
                          filters.provider_id ||
                          filters.customer_id ||
                          filters.service_type;

  const hasAdvancedFilters = filters.date_from || 
                            filters.date_to ||
                            filters.provider_id ||
                            filters.customer_id ||
                            filters.service_type;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            disabled={isLoading}
            className="text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Basic Filters - Always Visible */}
      <div className="space-y-4">
        {/* Search and Status Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Bookings
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, service..."
                value={filters.search}
                onChange={handleSearchChange}
                disabled={isLoading}
                className="pl-10 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:bg-gray-50"
              />
              {filters.search && (
                <button
                  onClick={() => handleInputChange('search', '')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={handleStatusChange}
              disabled={isLoading}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:bg-gray-50"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <div className="border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center">
              Advanced Filters
              {hasAdvancedFilters && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  Active
                </span>
              )}
            </span>
            {showAdvanced ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Advanced Filters - Collapsible */}
      {showAdvanced && (
        <div className="space-y-4 pt-4 border-t border-gray-200">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="date"
                    placeholder="From date"
                    value={filters.date_from}
                    onChange={handleDateFromChange}
                    disabled={isLoading}
                    className="pl-10 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:bg-gray-50"
                  />
                </div>
              </div>
              <div>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="date"
                    placeholder="To date"
                    value={filters.date_to}
                    onChange={handleDateToChange}
                    disabled={isLoading}
                    className="pl-10 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:bg-gray-50"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Admin-only filters */}
          {showAdminFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Provider Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Provider
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Provider ID or name..."
                    value={filters.provider_id || ''}
                    onChange={handleProviderChange}
                    disabled={isLoading}
                    className="pl-10 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:bg-gray-50"
                  />
                </div>
              </div>

              {/* Customer Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Customer ID or name..."
                    value={filters.customer_id || ''}
                    onChange={handleCustomerChange}
                    disabled={isLoading}
                    className="pl-10 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:bg-gray-50"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Service Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Type
            </label>
            <div className="relative">
              <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Service type or category..."
                value={filters.service_type || ''}
                onChange={handleServiceTypeChange}
                disabled={isLoading}
                className="pl-10 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:bg-gray-50"
              />
            </div>
          </div>
        </div>
      )}

      {/* Filter Summary for Mobile */}
      {hasActiveFilters && (
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {filters.search && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Search: "{filters.search.substring(0, 20)}{filters.search.length > 20 ? '...' : ''}"
                  <button
                    onClick={() => handleInputChange('search', '')}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filters.status !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Status: {statusOptions.find(s => s.value === filters.status)?.label}
                  <button
                    onClick={() => handleInputChange('status', 'all')}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filters.date_from && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  From: {filters.date_from}
                  <button
                    onClick={() => handleInputChange('date_from', '')}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filters.date_to && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  To: {filters.date_to}
                  <button
                    onClick={() => handleInputChange('date_to', '')}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingFilters;